import { IPropertyEditable } from "../../../../base/domain/system/edit";
import {
  IPrintBehavior,
  IEditBehavior,
  IFreeInputBehavior,
  INaturalTextBehavior,
  IEmptyAlertBehavior,
  ILengthAlertBehavior,
  ITooltipBehavior,
  IMultiLineBehavior,
  IInputRuleBehavior,
} from "../../../../documaker/domain/format/objects";
import { IFormatSpecification } from "../IFormatSpecification";
import { ICalculatableObject } from "../objects";
import {
  IPrintBehaviorPropertyEditable,
  IEditBehaviorPropertyEditable,
  IFreeInputBehaviorPropertyEditable,
  INaturalTextBehaviorPropertyEditable,
  IEmptyAlertBehaviorPropertyEditable,
  ILengthAlertBehaviorPropertyEditable,
  ITooltipBehaviorPropertyEditable,
  IMultiLineBehaviorPropertyEditable,
  IInputRuleBehaviorPropertyEditable,
} from "../properties";
import { AbstractTextDrawableObjectEditor } from "./AbstractTextDrawableObjectEditor";

export abstract class AbstractCalculatableObjectEditor<TObject extends ICalculatableObject> extends AbstractTextDrawableObjectEditor<TObject> implements
IPrintBehaviorPropertyEditable,
IEditBehaviorPropertyEditable,
IFreeInputBehaviorPropertyEditable,
INaturalTextBehaviorPropertyEditable,
IEmptyAlertBehaviorPropertyEditable,
ILengthAlertBehaviorPropertyEditable,
ITooltipBehaviorPropertyEditable,
IMultiLineBehaviorPropertyEditable,
IInputRuleBehaviorPropertyEditable {
  constructor(source: TObject | AbstractCalculatableObjectEditor<TObject>, specification?: IFormatSpecification, isNew: boolean = false) {
    super(source, specification, isNew);
    this._id = this.createIdPropertyEditor();
    this._name = this.createNamePropertyEditor();
    this._nameHistories = this.createNameHistoriesPropertyEditor();
    this._bookmark = this.createBookmarkPropertyEditor();
    this._printBehavior = this.createPrintBehaviorPropertyEditor(s => true);                // 既定は編集可に.必要な派生クラスでoverrideして制限する想定.
    this._editBehavior = this.createEditBehaviorPropertyEditor(s => true);                  // 既定は編集可に.必要な派生クラスでoverrideして制限する想定.
    this._freeInputBehavior = this.createFreeInputBehaviorPropertyEditor(s => true);        // 既定は編集可に.必要な派生クラスでoverrideして制限する想定.
    this._naturalTextBehavior = this.createNaturalTextBehaviorPropertyEditor(s => true);    // 既定は編集可に.必要な派生クラスでoverrideして制限する想定.
    this._emptyAlertBehavior = this.createEmptyAlertBehaviorPropertyEditor(s => true);      // 既定は編集可に.必要な派生クラスでoverrideして制限する想定.
    this._lengthAlertBehavior = this.createLengthAlertBehaviorPropertyEditor(s => true);    // 既定は編集可に.必要な派生クラスでoverrideして制限する想定.
    this._tooltipBehavior = this.createTooltipBehaviorPropertyEditor(s => true);            // 既定は編集可に.必要な派生クラスでoverrideして制限する想定.
    this._multiLineBehavior = this.createMultiLineBehaviorPropertyEditor(s => false);       // 既定は編集不可に.必要な派生クラスでoverrideする想定.
    this._inputRuleBehavior = this.createInputRuleBehaviorPropertyEditor(s => false);       // 既定は編集不可に.必要な派生クラスでoverrideする想定.
  }

  protected _printBehavior: IPropertyEditable<IPrintBehavior>;
  protected _editBehavior: IPropertyEditable<IEditBehavior>;
  protected _freeInputBehavior: IPropertyEditable<IFreeInputBehavior>;
  protected _naturalTextBehavior: IPropertyEditable<INaturalTextBehavior>;
  protected _emptyAlertBehavior: IPropertyEditable<IEmptyAlertBehavior>;
  protected _lengthAlertBehavior: IPropertyEditable<ILengthAlertBehavior>;
  protected _tooltipBehavior: IPropertyEditable<ITooltipBehavior>;
  protected _multiLineBehavior: IPropertyEditable<IMultiLineBehavior>;
  protected _inputRuleBehavior: IPropertyEditable<IInputRuleBehavior>;

  protected createPrintBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IPrintBehavior> {
    return this.createPropertyEditor(
      s => s.printBehavior,
      (s, v) => s.printBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createEditBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IEditBehavior> {
    return this.createPropertyEditor(
      s => s.editBehavior,
      (s, v) => s.editBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createFreeInputBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IFreeInputBehavior> {
    return this.createPropertyEditor(
      s => s.freeInputBehavior,
      (s, v) => s.freeInputBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createNaturalTextBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<INaturalTextBehavior> {
    return this.createPropertyEditor(
      s => s.naturalTextBehavior,
      (s, v) => s.naturalTextBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createEmptyAlertBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IEmptyAlertBehavior> {
    return this.createPropertyEditor(
      s => s.emptyAlertBehavior,
      (s, v) => s.emptyAlertBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createLengthAlertBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<ILengthAlertBehavior> {
    return this.createPropertyEditor(
      s => s.lengthAlertBehavior,
      (s, v) => s.lengthAlertBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createTooltipBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<ITooltipBehavior> {
    return this.createPropertyEditor(
      s => s.tooltipBehavior,
      (s, v) => s.tooltipBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createMultiLineBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IMultiLineBehavior> {
    return this.createPropertyEditor(
      s => s.multiLineBehavior,
      (s, v) => s.multiLineBehavior = v,
      isWritable ? isWritable : s => true);
  }

  protected createInputRuleBehaviorPropertyEditor(isWritable?: (s: TObject) => boolean): IPropertyEditable<IInputRuleBehavior> {
    return this.createPropertyEditor(
      s => s.inputRuleBehavior,
      (s, v) => s.inputRuleBehavior = v,
      isWritable ? isWritable : s => true);
  }

  printBehavior(): IPropertyEditable<IPrintBehavior> {
    return this._printBehavior;
  }

  editBehavior(): IPropertyEditable<IEditBehavior> {
    return this._editBehavior;
  }

  freeInputBehavior(): IPropertyEditable<IFreeInputBehavior> {
    return this._freeInputBehavior;
  }

  naturalTextBehavior(): IPropertyEditable<INaturalTextBehavior> {
    return this._naturalTextBehavior;
  }

  emptyAlertBehavior(): IPropertyEditable<IEmptyAlertBehavior> {
    return this._emptyAlertBehavior;
  }

  lengthAlertBehavior(): IPropertyEditable<ILengthAlertBehavior> {
    return this._lengthAlertBehavior;
  }

  tooltipBehavior(): IPropertyEditable<ITooltipBehavior> {
    return this._tooltipBehavior;
  }

  multiLineBehavior(): IPropertyEditable<IMultiLineBehavior> {
    return this._multiLineBehavior;
  }

  inputRuleBehavior(): IPropertyEditable<IInputRuleBehavior> {
    return this._inputRuleBehavior;
  }
}
